
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Shell } from "@/components/layout/Shell";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Settings } from "lucide-react";

const profileFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address").min(1, "Email is required"),
  companyName: z.string().min(1, "Company name is required"),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

const ProfilePage = () => {
  const { t } = useTranslation();
  const { user, isLoading, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      name: user?.name || "",
      email: user?.email || "",
      companyName: user?.companyName || "",
    },
    values: {
      name: user?.name || "",
      email: user?.email || "",
      companyName: user?.companyName || "",
    },
  });

  async function onSubmit(data: ProfileFormValues) {
    setIsSubmitting(true);
    try {
      await updateProfile({
        name: data.name,
        companyName: data.companyName,
      });
      
      toast.success(t('profileUpdated'));
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoading) {
    return (
      <Shell>
        <div className="flex items-center justify-center min-h-[50vh]">
          <p>{t('loading')}</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="w-full max-w-3xl mx-auto py-6 px-4 sm:px-6 md:py-10">
        <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">{t('profile')}</h1>
        
        <Card className="w-full mb-6">
          <CardHeader>
            <CardTitle>{t('yourInformation')}</CardTitle>
            <CardDescription>{t('editPersonalInfo')}</CardDescription>
          </CardHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('name')}</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isSubmitting} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('companyName')}</FormLabel>
                      <FormControl>
                        <Input {...field} disabled={isSubmitting} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('email')}</FormLabel>
                      <FormControl>
                        <Input {...field} disabled type="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
              <CardFooter className="flex flex-col sm:flex-row gap-3 justify-between">
                <Button 
                  variant="outline" 
                  onClick={() => navigate(-1)}
                  className="w-full sm:w-auto"
                >
                  {t('cancel')}
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full sm:w-auto"
                >
                  {isSubmitting ? t('saving') : t('saveChanges')}
                </Button>
              </CardFooter>
            </form>
          </Form>
        </Card>
        
        <div className="mt-6 flex items-center gap-2 justify-center">
          <Button asChild variant="outline">
            <Link to="/settings" className="flex items-center text-primary">
              <Settings className="mr-2 h-4 w-4" />
              <span>Go to settings</span>
            </Link>
          </Button>
        </div>
      </div>
    </Shell>
  );
};

export default ProfilePage;
